import { AVAILABLE_ANIMATIONS } from '@/constants/animations';
import { DYNAMIC_EFFECT_IDS, LOOK_EFFECT_IDS } from '@/lib/video-effects/sections';
import { LUT_PRESET_IDS } from '@/lib/video-effects/types';
import { TRANSITION_KINDS } from '@/lib/transitions/types';
import { SUBTITLE_TEXT_TRANSFORMS } from '@/lib/subtitles/subtitleTextTransform';
import type { ClientToolDefinition } from '../clientTools';
import { CAPTURE_TOOL, MAX_CAPTURE_FRAMES } from './frameCapture';
import { buildLayoutCatalog } from './layoutCatalog';
import { MEDIA_PLACEMENTS } from './mediaTools';
import { motionToolDefinitions } from './motionToolDefinitions';
import { SFX_ALIGNS, SFX_CATEGORIES } from './soundEffects';

// The editor's browser tools. Enums come from the same constants the editor
// UI renders, so the model can only name options that exist. Times are
// seconds on the clip timeline (0 = clip start), the axis text layers and
// effect sections live on.

export const SUBTITLE_POSITIONS = ['top', 'middle', 'bottom'] as const;
export const TEXT_POSITIONS = ['top', 'upper', 'center', 'lower', 'bottom'] as const;

const HEX = 'Hex color like #FFD400';

const time = (description: string) => ({ type: 'number', minimum: 0, description });

// Calls in one reply run in order, so a timeline call shifts the scenes and
// times the next call sees. Said in every tool that takes indexes or times.
const FRAMING = {
  type: 'object',
  properties: {
    x: { type: 'number', minimum: 0, maximum: 100 },
    y: { type: 'number', minimum: 0, maximum: 100 },
    zoom: { type: 'number', minimum: 1, maximum: 3 },
  },
};

const IN_ORDER = ' Calls in one reply run in order: after a cut, scene indexes and times have moved.';

const RECT = {
  type: 'object',
  description: 'Fractions of the frame: x, y = top-left corner, w, h = size (1 = full width/height)',
  properties: { x: { type: 'number' }, y: { type: 'number' }, w: { type: 'number' }, h: { type: 'number' } },
  required: ['x', 'y', 'w', 'h'],
};

export function buildEditorToolDefinitions(): ClientToolDefinition[] {
  const animations = AVAILABLE_ANIMATIONS.map((animation) => animation.name);
  const effects = [...DYNAMIC_EFFECT_IDS, ...LOOK_EFFECT_IDS];
  return [
    ...motionToolDefinitions,
    {
      name: 'editor_set_subtitle_style',
      description: 'Change how the clip subtitles look (one style for every line). Only the fields '
        + 'you pass change. font must be one of options.fonts in the page state. To copy captions '
        + 'that stack lines of different size, color or font, use editor_add_captions instead.',
      parameters: {
        type: 'object',
        properties: {
          enabled: { type: 'boolean', description: 'Show subtitles' },
          font: { type: 'string', description: 'Font name from options.fonts' },
          size: { type: 'number', minimum: 16, maximum: 140 },
          text_color: { type: 'string', description: HEX },
          highlight_enabled: { type: 'boolean', description: 'Color the spoken word' },
          highlight_color: { type: 'string', description: HEX },
          highlight_box: { type: 'boolean', description: 'Box behind the spoken word' },
          highlight_box_color: { type: 'string', description: HEX },
          animation: { type: 'string', enum: animations },
          position: { type: 'string', enum: [...SUBTITLE_POSITIONS] },
          text_transform: { type: 'string', enum: [...SUBTITLE_TEXT_TRANSFORMS] },
          outline: { type: 'boolean' },
          outline_color: { type: 'string', description: HEX },
          outline_width: { type: 'number', minimum: 0, maximum: 30, description: '% of font size' },
          shadow: { type: 'boolean' },
          background: { type: 'boolean', description: 'Box behind the whole line' },
          background_color: { type: 'string', description: HEX },
          background_opacity: { type: 'number', minimum: 0, maximum: 100 },
          max_words_per_line: { type: 'integer', minimum: 1, maximum: 12 },
        },
      },
    },
    {
      name: 'editor_apply_template',
      description: 'Apply a saved template (brand kit) to the clip: subtitle style, logo and layout. '
        + 'template is an id or name from options.templates. Style tweaks after it go in a later step.',
      parameters: {
        type: 'object',
        properties: { template: { type: 'string' } },
        required: ['template'],
      },
    },
    {
      name: 'editor_add_text',
      description: 'Add a text layer (a caption, a reaction like "KKKKK" or an emoji) on screen '
        + 'from start to end. Keep reactions short (1-4 words) and on screen 1-3 s.',
      parameters: {
        type: 'object',
        properties: {
          text: { type: 'string', maxLength: 120 },
          start: time('Seconds from the clip start'),
          end: time('Seconds from the clip start'),
          position: { type: 'string', enum: [...TEXT_POSITIONS] },
          size: { type: 'number', minimum: 20, maximum: 220, description: 'px at 1080p (default 72)' },
          color: { type: 'string', description: HEX },
          background_color: { type: 'string', description: `${HEX}; omit for none` },
          outline_color: { type: 'string', description: HEX },
          outline_width: { type: 'number', minimum: 0, maximum: 30, description: 'px (default 6)' },
          font: { type: 'string', description: 'Font name from options.fonts' },
          bold: { type: 'boolean' },
          rotation: { type: 'number', minimum: -30, maximum: 30, description: 'Degrees' },
        },
        required: ['text', 'start', 'end'],
      },
    },
    {
      name: 'editor_update_text',
      description: 'Change a text layer by id (from page state texts or editor_add_text). '
        + 'Only the fields you pass change.',
      parameters: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          text: { type: 'string', maxLength: 120 },
          start: time('Seconds from the clip start'),
          end: time('Seconds from the clip start'),
          position: { type: 'string', enum: [...TEXT_POSITIONS] },
          size: { type: 'number', minimum: 20, maximum: 220 },
          color: { type: 'string', description: HEX },
          background_color: { type: 'string', description: `${HEX}; "none" removes it` },
          outline_color: { type: 'string', description: HEX },
          outline_width: { type: 'number', minimum: 0, maximum: 30 },
          font: { type: 'string', description: 'Font name from options.fonts' },
          bold: { type: 'boolean' },
          rotation: { type: 'number', minimum: -30, maximum: 30 },
        },
        required: ['id'],
      },
    },
    {
      name: 'editor_remove_layer',
      description: 'Remove a layer (text, image, video or audio) by id.',
      parameters: { type: 'object', properties: { id: { type: 'string' } }, required: ['id'] },
    },
    {
      name: 'editor_add_effect',
      description: 'Add a timeline effect from start to end. Dynamic: shake, zoom-punch, flash, '
        + 'glitch, rgb-split, focus-pull, strobe, vhs (emphasis, best 0.3-1.5 s on a punchline). '
        + 'Looks: look-* color grades, bw, grain, vignette, blur (any length).',
      parameters: {
        type: 'object',
        properties: {
          effect: { type: 'string', enum: effects },
          start: time('Seconds from the clip start'),
          end: time('Seconds from the clip start'),
          intensity: { type: 'number', minimum: 0, maximum: 100, description: 'Default 80 (dynamic) / 100 (looks)' },
        },
        required: ['effect', 'start', 'end'],
      },
    },
    {
      name: 'editor_remove_effect',
      description: 'Remove a timeline effect by id (from page state effects or editor_add_effect).',
      parameters: { type: 'object', properties: { id: { type: 'string' } }, required: ['id'] },
    },
    {
      name: 'editor_set_transition',
      description: 'Set the transition at a cut between scenes (after_scene = index of the scene '
        + 'before the cut, from page state scenes), or at every cut with all=true. kind "none" '
        + 'removes it.',
      parameters: {
        type: 'object',
        properties: {
          after_scene: { type: 'integer', minimum: 0 },
          all: { type: 'boolean' },
          kind: { type: 'string', enum: [...TRANSITION_KINDS, 'none'] },
          duration: { type: 'number', minimum: 0.1, maximum: 1.5, description: 'Seconds (default 0.3)' },
          direction: { type: 'string', enum: ['left', 'right', 'up', 'down'] },
        },
        required: ['kind'],
      },
    },
    {
      name: 'editor_set_look',
      description: 'Set the color look of the whole clip: a preset ("none" removes it) and/or '
        + 'adjustments. Only the fields you pass change.',
      parameters: {
        type: 'object',
        properties: {
          preset: { type: 'string', enum: [...LUT_PRESET_IDS, 'none'] },
          strength: { type: 'number', minimum: 0, maximum: 100 },
          exposure: { type: 'number', minimum: -2, maximum: 2 },
          contrast: { type: 'number', minimum: -100, maximum: 100 },
          saturation: { type: 'number', minimum: -100, maximum: 100 },
          temperature: { type: 'number', minimum: -100, maximum: 100 },
          vibrance: { type: 'number', minimum: -100, maximum: 100 },
          vignette: { type: 'number', minimum: 0, maximum: 100 },
          grain: { type: 'number', minimum: 0, maximum: 100 },
          grayscale: { type: 'number', minimum: 0, maximum: 100 },
        },
      },
    },
    {
      name: 'editor_cut_ranges',
      description: 'Remove parts of the clip (e.g. pauses from page state silences, or a dull '
        + 'stretch). Ranges are clip-timeline seconds; a cut inside a scene splits it. Put EVERY '
        + 'range in this one call (all times from the page state); a second cut call in the same '
        + 'reply would use times that already moved.',
      parameters: {
        type: 'object',
        properties: {
          ranges: {
            type: 'array',
            minItems: 1,
            maxItems: 80,
            items: {
              type: 'object',
              properties: { start: time('Seconds from the clip start'), end: time('Seconds from the clip start') },
              required: ['start', 'end'],
            },
          },
        },
        required: ['ranges'],
      },
    },
    {
      name: 'editor_trim',
      description: 'Make the clip start later and/or end earlier (new start/end on the clip timeline).',
      parameters: {
        type: 'object',
        properties: { start: time('New clip start'), end: time('New clip end') },
      },
    },
    {
      name: 'editor_extend',
      description: 'Add footage from the source video before the clip start and/or after its end '
        + '(seconds; see context_before/context_after for what is said there).',
      parameters: {
        type: 'object',
        properties: {
          before: { type: 'number', minimum: 0, maximum: 120 },
          after: { type: 'number', minimum: 0, maximum: 120 },
        },
      },
    },
    {
      name: 'editor_split',
      description: 'Split the scene under a clip time into two scenes (then they can get a '
        + `transition, different framing or a new order).${IN_ORDER}`,
      parameters: { type: 'object', properties: { time: time('Seconds from the clip start') }, required: ['time'] },
    },
    {
      name: 'editor_remove_scene',
      description: 'Remove scenes by index (from page state scenes). To remove several, pass them '
        + `all in indexes in ONE call.${IN_ORDER}`,
      parameters: {
        type: 'object',
        properties: {
          indexes: { type: 'array', items: { type: 'integer', minimum: 0 }, minItems: 1, maxItems: 60 },
          index: { type: 'integer', minimum: 0, description: 'One scene (same as indexes: [index])' },
        },
      },
    },
    {
      name: 'editor_reorder_scenes',
      description: `Play the scenes in a new order: order lists every current scene index once.${IN_ORDER}`,
      parameters: {
        type: 'object',
        properties: { order: { type: 'array', items: { type: 'integer', minimum: 0 }, maxItems: 60 } },
        required: ['order'],
      },
    },
    {
      name: 'editor_camera_move',
      description: 'Move the camera inside one scene of a vertical clip cut from a wider video: a smooth '
        + 'push-in, pull-out or pan across the whole scene, from its current framing (or `from`) to `to`. '
        + 'x/y is the point kept at the center (% of the source frame), zoom 1-3. Subtle moves look '
        + 'natural (zoom +0.1 to +0.3 on the speaker). editor_set_framing on that scene removes the move. '
        + `Exporting camera moves needs PRO, like transitions.${IN_ORDER}`,
      parameters: {
        type: 'object',
        properties: {
          scene: { type: 'integer', minimum: 0 },
          from: FRAMING,
          to: FRAMING,
        },
        required: ['scene', 'to'],
      },
    },
    {
      name: 'editor_set_framing',
      description: 'Reframe one scene of a vertical clip cut from a wider video: x/y is the point '
        + 'kept at the center (% of the source frame, 50/50 = center), zoom 1-3. Only that scene '
        + `changes. Use a frame capture to see where the subject is.${IN_ORDER}`,
      parameters: {
        type: 'object',
        properties: {
          scene: { type: 'integer', minimum: 0 },
          x: { type: 'number', minimum: 0, maximum: 100 },
          y: { type: 'number', minimum: 0, maximum: 100 },
          zoom: { type: 'number', minimum: 1, maximum: 3 },
        },
        required: ['scene'],
      },
    },
    {
      name: 'editor_search_media',
      description: 'Search images or videos (stock: Pexels/Unsplash), the user\'s own uploads '
        + '(my_media, also audio) or Autoclipper\'s sound effects (sfx: kind audio, optional '
        + 'category; audio searches sfx by default). Returns up to 8 items with item_id. '
        + 'Images/videos show you up to 4 thumbnails; sound effects give category, tags, '
        + 'duration and peak_at (seconds from their start to the hit). Search in English for stock.',
      parameters: {
        type: 'object',
        properties: {
          kind: { type: 'string', enum: ['image', 'video', 'audio'] },
          query: { type: 'string', maxLength: 100 },
          source: { type: 'string', enum: ['stock', 'my_media', 'sfx'] },
          category: { type: 'string', enum: [...SFX_CATEGORIES] },
        },
        required: ['kind'],
      },
    },
    {
      name: 'editor_add_media',
      description: 'Add a found item (item_id from editor_search_media) as a layer from start to '
        + 'end. placement: fill = b-roll over the whole frame (default), fit = whole media inside '
        + 'the frame, pip = small picture in the top-right corner, rect = your rect (fractions of '
        + 'the frame). Videos come muted unless you set volume; audio defaults to 30%. Sound '
        + 'effects (source sfx) default to 60%, play once, and with align "peak" their hit '
        + 'lands on start instead of their beginning. At most 2 sound effects at once.',
      parameters: {
        type: 'object',
        properties: {
          item_id: { type: 'string' },
          start: time('Seconds from the clip start'),
          end: time('Seconds from the clip start'),
          placement: { type: 'string', enum: [...MEDIA_PLACEMENTS] },
          rect: RECT,
          volume: { type: 'number', minimum: 0, maximum: 100 },
          opacity: { type: 'number', minimum: 0, maximum: 100 },
          align: { type: 'string', enum: [...SFX_ALIGNS] },
        },
        required: ['item_id'],
      },
    },
    {
      name: 'editor_update_layer',
      description: 'Change any layer by id (texts, images, videos, audio): timing, placement or '
        + 'rect, opacity, volume, speed (0.25-4). Text content and style: editor_update_text.',
      parameters: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          start: time('Seconds from the clip start'),
          end: time('Seconds from the clip start'),
          placement: { type: 'string', enum: [...MEDIA_PLACEMENTS] },
          rect: RECT,
          opacity: { type: 'number', minimum: 0, maximum: 100 },
          volume: { type: 'number', minimum: 0, maximum: 100 },
          speed: { type: 'number', minimum: 0.25, maximum: 4 },
        },
        required: ['id'],
      },
    },
    {
      name: 'editor_set_layout',
      description: 'Put the clip in a post layout (kind and preset from options.layouts; "none" '
        + 'removes it) and fill its fields: [{ key, value }] with keys from that layout\'s fields '
        + '(text, #hex colors, numbers, option values, font names; images take an item_id from '
        + 'editor_search_media).',
      parameters: {
        type: 'object',
        properties: {
          kind: { type: 'string', enum: [...buildLayoutCatalog().map((entry) => entry.kind), 'none'] },
          preset: { type: 'string' },
          fields: {
            type: 'array',
            maxItems: 20,
            items: {
              type: 'object',
              properties: { key: { type: 'string' }, value: { type: 'string' } },
              required: ['key', 'value'],
            },
          },
        },
        required: ['kind'],
      },
    },
    {
      name: 'editor_set_cover',
      description: 'Use the frame at a clip time (as the preview draws it) as the clip cover '
        + '(the first frame TikTok/Reels/Shorts show).',
      parameters: { type: 'object', properties: { time: time('Seconds from the clip start') }, required: ['time'] },
    },
    {
      name: 'editor_seek',
      description: 'Move the preview to a time on the clip timeline, optionally playing it, '
        + 'so the user sees the result.',
      parameters: {
        type: 'object',
        properties: { time: time('Seconds from the clip start'), play: { type: 'boolean' } },
        required: ['time'],
      },
    },
    {
      name: CAPTURE_TOOL,
      description: 'Capture up to 4 frames of the clip exactly as the preview shows them (layout, '
        + 'subtitles, texts, effects) and look at them. Omit times for the current playhead. Use it '
        + 'before editing when what is on screen matters and after visual edits to check them. '
        + 'Captures run after the other edits of the same reply. A frame with source "video" is the '
        + 'raw footage: subtitles, texts, layout and effects are not in it.',
      parameters: {
        type: 'object',
        properties: {
          times: {
            type: 'array',
            maxItems: MAX_CAPTURE_FRAMES,
            items: { type: 'number', minimum: 0 },
            description: 'Seconds from the clip start',
          },
        },
      },
    },
    {
      name: 'editor_select_clip',
      description: 'Open another clip of this project in the editor (index from page state clips). '
        + 'Call it alone; the next step sees the new clip.',
      parameters: {
        type: 'object',
        properties: { index: { type: 'integer', minimum: 1 } },
        required: ['index'],
      },
    },
  ];
}
